// Ocorrências do sistema, do lado da tela.
//
// A frase que o usuário lê vem do catálogo no banco (ocorrencia_tipos), não
// daqui: é a mesma fonte que o Eugênio usa para avisar o TI. Assim o texto é
// escrito uma vez e não há como a tela dizer uma coisa e o alerta outra.
//
// O que este módulo faz é só transporte e formatação: reconhecer a ocorrência
// que veio na resposta de uma edge function, montar o bloco que o usuário
// copia para o TI, e registrar ocorrência que só a tela consegue perceber.

import { supabase } from '@/integrations/supabase/client';
// Mesma lista de mensagens que decide o recarregamento automático: duas cópias
// divergiriam, e a assinatura pararia de agrupar sem ninguém notar.
import { isChunkError } from '@/lib/chunkRecarrega';

export type OcorrenciaResumo = {
  codigo: string;
  titulo: string;
  mensagemUsuario: string;
  acaoUsuario: string | null;
  /** 'fornecedor' = não há o que o TI corrija; 'nosso' = tem dono aqui dentro. */
  dono: string;
  contagem?: number;
  /** Se o Eugênio já avisou o canal de ocorrências por esta falha. */
  notificada?: boolean;
  ocorrenciaId?: string | null;
};

/**
 * Extrai a ocorrência que a edge function anexou à resposta de erro.
 * Devolve null quando a resposta não traz ocorrência — resposta antiga,
 * função ainda não publicada, ou erro que não foi catalogado.
 */
export function extrairOcorrencia(payload: unknown): OcorrenciaResumo | null {
  const oc = (payload as any)?.ocorrencia;
  if (!oc || typeof oc !== 'object' || !oc.codigo) return null;
  return {
    codigo: String(oc.codigo),
    titulo: String(oc.titulo || oc.codigo),
    mensagemUsuario: String(oc.mensagemUsuario || 'A operação falhou por um problema no sistema.'),
    acaoUsuario: oc.acaoUsuario ? String(oc.acaoUsuario) : null,
    dono: String(oc.dono || 'nosso'),
    contagem: typeof oc.contagem === 'number' ? oc.contagem : undefined,
    notificada: !!oc.notificada,
    ocorrenciaId: oc.ocorrenciaId ?? null,
  };
}

/** Uma linha por código: a mesma falha em quatro consultas é uma ocorrência. */
export function juntarOcorrencias(lista: (OcorrenciaResumo | null)[]): OcorrenciaResumo[] {
  const porCodigo = new Map<string, OcorrenciaResumo>();
  for (const oc of lista) {
    if (!oc) continue;
    const anterior = porCodigo.get(oc.codigo);
    if (!anterior) {
      porCodigo.set(oc.codigo, oc);
      continue;
    }
    porCodigo.set(oc.codigo, {
      ...anterior,
      contagem: Math.max(anterior.contagem ?? 1, oc.contagem ?? 1),
      notificada: anterior.notificada || oc.notificada,
    });
  }
  return [...porCodigo.values()];
}

/**
 * Bloco de texto que o usuário copia e cola para o TI.
 *
 * Só identificadores no contexto (id de empresa, competência, tela). Nome de
 * cliente, CNPJ e conteúdo de tabela não entram: isto circula por chat.
 */
export function textoParaTI(
  ocorrencias: OcorrenciaResumo[],
  contexto: Record<string, string | number | null | undefined>,
): string {
  const agora = new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  const linhas: string[] = ['EcoWeb — ocorrência do sistema'];

  for (const [chave, valor] of Object.entries(contexto)) {
    if (valor === null || valor === undefined || valor === '') continue;
    linhas.push(`${chave}: ${valor}`);
  }
  linhas.push(`Data/hora: ${agora}`);

  for (const oc of ocorrencias) {
    const repeticoes = oc.contagem && oc.contagem > 1 ? ` (${oc.contagem} repetições)` : '';
    linhas.push(`Falha: ${oc.titulo} [${oc.codigo}]${repeticoes}`);
    // O id é o que o TI usa para achar a pilha inteira e o corpo da resposta
    // do fornecedor, que ficam gravados na ocorrência. Colar a pilha aqui
    // encheria o chat e ainda seria só o começo dela.
    if (oc.ocorrenciaId) linhas.push(`Registro: ${oc.ocorrenciaId}`);
  }

  const algumNotificado = ocorrencias.some((o) => o.notificada);
  linhas.push(
    algumNotificado
      ? 'O Eugênio já avisou o canal de ocorrências.'
      : 'O canal de ocorrências ainda não foi avisado por esta falha.',
  );

  return linhas.join('\n');
}

export type DestinoConfigurado = { tipo: string; ref: string };
export type DestinoEfetivo = { tipo: string; ref: string; herdado: boolean };

/**
 * Quem um alerta avisa de fato.
 *
 * A lista configurada manda. Vazia, vale o par destino_tipo/destino_ref do
 * catálogo — é o que mantém avisando os alertas que nasceram antes da tela de
 * configuração existir. O `herdado` marca esse caso, para a tela poder dizer
 * que aquele destino é o padrão, e não escolha de alguém.
 *
 * Precisa espelhar resolverConversas em
 * supabase/functions/_shared/ocorrencias.ts: a tela mostrando um destino
 * diferente do que o Eugênio usa é pior que não mostrar nada.
 */
export function destinosEfetivos(
  tipo: { destino_tipo: string; destino_ref: string | null },
  configurados: DestinoConfigurado[],
): DestinoEfetivo[] {
  if (configurados.length > 0) {
    return configurados.map((d) => ({ tipo: d.tipo, ref: d.ref, herdado: false }));
  }
  if (tipo.destino_tipo === 'ninguem') return [];
  return [{ tipo: tipo.destino_tipo, ref: tipo.destino_ref || 'ocorrencias', herdado: true }];
}

/**
 * Assinatura de uma tela que quebrou, feita para agrupar.
 *
 * A mensagem crua não serve: quando o erro é chunk que sumiu no deploy, ela
 * carrega a URL do arquivo com o hash do build
 * (`.../assets/ChatInterno-DX8qjA48.js`). Hash é diferente a cada build e a
 * cada página, então cada deploy inventava assinaturas novas — o agrupamento
 * não agrupava nada e o TI recebia um alerta por aba afetada.
 *
 * Chunk ausente vira uma assinatura só. Qual rota e qual arquivo continuam no
 * detalhe da ocorrência, que é onde se investiga; a assinatura é só a
 * identidade do agrupamento.
 */
export function assinaturaDeTela(error: { name?: string; message?: string }): string {
  const mensagem = String(error?.message ?? '');
  if (isChunkError(mensagem)) return 'ecoweb|tela|chunk_ausente';
  // Erro que cita URL sem ser de chunk (fetch falho, por exemplo) também não
  // deve trazer host e hash para dentro da chave de agrupamento.
  const semUrl = mensagem.replace(/https?:\/\/\S+/g, '[url]');
  return `ecoweb|tela|${error?.name ?? 'Error'}|${semUrl}`.slice(0, 200);
}

/**
 * Registra uma ocorrência que só a tela consegue perceber — a edge function
 * não respondeu, por exemplo. Não lança e não espera: a tela já está tratando
 * um erro, e o registro não pode virar um segundo erro.
 */
export async function reportarOcorrencia(
  codigo: string,
  opts: {
    assinatura?: string;
    origem?: string;
    detalhe?: Record<string, unknown>;
    /**
     * Pilha do erro. Vale a pena porque o build passou a gerar sourcemap: sem
     * ele o que chegaria ao TI seria `index-B7dK2p.js:48:11923`.
     */
    stack?: string | null;
    /** Frase pronta para quem recebe, um fato por linha (tipo de negócio). */
    resumo?: string;
    /** Como chamar quem disparou, no lugar de "Quem estava usando". */
    rotuloUsuario?: string;
    /** Não identificar quem disparou (sugestão anônima): nem nome, nem registro. */
    anonimo?: boolean;
  } = {},
): Promise<string | null> {
  try {
    const { data } = await supabase.functions.invoke('registrar-ocorrencia', {
      body: {
        codigo,
        assinatura: opts.assinatura || codigo,
        origem: opts.origem,
        detalhe: opts.detalhe,
        stack: opts.stack ?? null,
        resumo: opts.resumo,
        rotuloUsuario: opts.rotuloUsuario,
        anonimo: opts.anonimo === true ? true : undefined,
      },
    });
    // Devolve o id para a tela poder mostrar ao usuário o número do registro,
    // que é como o TI acha a pilha inteira depois.
    return (data as { ocorrenciaId?: string } | null)?.ocorrenciaId ?? null;
  } catch (e) {
    console.error('[ocorrencias] falha ao reportar', codigo, e);
    return null;
  }
}
